<template>
    <div class="record-viewer" @click.stop="$_.noop">
        <a-tabs :default-active-key="defaultOpenTab || 'record'">
            <a-tab-pane key="record" :tab="$t('modules.recordDetail')">
                <a-spin :spinning="loading" size="large">
                    <div v-if="table && record" class="record-container">
                        <div class="field-grid">
                            <div class="field-item" v-for="field in table.fields" :key="field._id">
                                <label class="field-label">
                                    <FieldIcon :type="field.fieldType" class="field-icon" :default-icon="field.icon" />
                                    <span>{{ field.name }}</span>
                                </label>
                                <component
                                    :is="getCellComponent(field.fieldType)"
                                    :field="field"
                                    :record="record"
                                    style="min-height: 28px"
                                    outlined
                                    v-if="record"
                                    @update-record="handleRecordUpdate"
                                />
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-info">{{ $t("modules.recordNotFound") }}</div>
                </a-spin>
            </a-tab-pane>
            <a-tab-pane key="related-lists" :tab="$t('modules.relatedLists')">
                <ModuleRelatedList :tableId="tableId" :recordId="recordId" :record="record" v-if="record" />
            </a-tab-pane>
            <a-tab-pane key="comments" :tab="commentTabTitle">
                <RecordComments :recordId="recordId" v-if="record" @comments-loaded="handleCommentsLoaded" />
            </a-tab-pane>
            <a-tab-pane key="tasks" :tab="'Görevler'">
                <TableTask :recordId="recordId"/>
            </a-tab-pane>
        </a-tabs>
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../api/table";
import FieldIcon from "../FieldIcon.vue";
import ModuleRelatedList from "./ModuleRelatedList.vue";
import RecordComments from "./comments/RecordComments.vue";
import TableTask from "./components/TableTask.vue";
import { listComments } from "@/api/comment";

// Var olan hücre bileşenlerini tek tek içe aktarıyoruz
import StringCell from "./cells/StringCell.vue";
import NumberCell from "./cells/NumberCell.vue";
import CheckboxCell from "./cells/CheckboxCell.vue";
import DateCell from "./cells/DateCell.vue";
import DropdownCell from "./cells/DropdownCell.vue";
import AssetCell from "./cells/AssetCell.vue";
import ButtonCell from "./cells/ButtonCell.vue";
import ContactCell from "./cells/ContactCell.vue";
import TaskCell from "./cells/TaskCell.vue";
import UserCell from "./cells/UserCell.vue";
import CurrencyCell from "./cells/CurrencyCell.vue";
import LinkedRecordCell from "./cells/LinkedRecordCell.vue";
import JsonCell from "./cells/JsonCell.vue";
import { mapState } from "vuex";

export default Vue.extend({
    name: "RecordViewer",
    components: {
        StringCell,
        NumberCell,
        CheckboxCell,
        DateCell,
        DropdownCell,
        AssetCell,
        ButtonCell,
        ContactCell,
        TaskCell,
        UserCell,
        CurrencyCell,
        LinkedRecordCell,
        JsonCell,
        FieldIcon,
        ModuleRelatedList,
        RecordComments,
        TableTask,
        ConversationCell: () => import("./cells/ConversationCell.vue"),
    },
    props: {
        tableId: {
            type: String,
            required: true,
        },
        recordId: {
            type: String,
            required: true,
        },
        defaultOpenTab: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            loading: true,
            record: null,
            table: null,
            commentCount: 0,
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
        /**
         * table.fields dizisini 2'li kolonlar halinde böler
         */
        rows() {
            if (!this.table || !this.table.fields) {
                return [];
            }
            var result = [];
            var currentRow = [];
            for (var i = 0; i < this.table.fields.length; i++) {
                currentRow.push(this.table.fields[i]);
                if (currentRow.length === 2) {
                    result.push(currentRow);
                    currentRow = [];
                }
            }
            if (currentRow.length > 0) {
                result.push(currentRow);
            }
            return result;
        },
        commentTabTitle() {
            return this.commentCount > 0
                ? `${this.$t('Comments')} (${this.commentCount})`
                : this.$t('Comments');
        }
    },
    methods: {
        /**
         * Sunucudan tablo ve kayıt bilgisi alır.
         */
        async fetchData() {
            try {
                this.loading = true;

                // Kayıt bilgisi
                this.record = await TABLE_API.getRecordById(this.tableId, this.recordId);
                await this.fetchCommentCount();
            } catch (error) {
                console.error("Kayıt veya tablo bilgisi alınırken hata oluştu:", error);
                this.$message.error("Kayıt veya tablo bilgisi alınırken hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        /**
         * Alan tipine göre uygun bileşeni döndürür.
         * cells klasöründe yer alan bileşenleri kullanıyoruz.
         */
        getCellComponent(fieldType) {
            if (
                fieldType === "text" ||
                fieldType === "long-text" ||
                fieldType === "email" ||
                fieldType === "phone" ||
                fieldType === "url"
            ) {
                return "StringCell";
            }
            if (fieldType === "json") {
                return "JsonCell";
            }
            if (fieldType === "number") {
                return "NumberCell";
            }
            if (fieldType === "datetime") {
                return "DateCell";
            }
            if (fieldType === "dropdown") {
                return "DropdownCell";
            }
            if (fieldType === "checkbox") {
                return "CheckboxCell";
            }
            if (fieldType === "file" || fieldType === "image" || fieldType === "video" || fieldType === "audio") {
                return "AssetCell";
            }
            if (fieldType === "contact") {
                return "ContactCell";
            }
            if (fieldType === "task") {
                return "TaskCell";
            }
            if (fieldType === "user") {
                return "UserCell";
            }

            if (fieldType === "currency") {
                return "CurrencyCell";
            }
            if (fieldType === "linked-record") {
                return "LinkedRecordCell";
            }

            if (fieldType === "automation-button") {
                return "ButtonCell";
            }
            if (fieldType === "conversation") {
                return "ConversationCell";
            }
            // Varsayılan olarak metin hücresini dön
            return "StringCell";
        },
        /**
         * Hücre bileşenlerinden "update-record" etkinliği tetiklendiğinde yakalayıp,
         * sunucuya güncelleme isteği gönderir ve ardından local state'i günceller.
         */
        async handleRecordUpdate(payload) {
            try {
                const updatingFields = {
                    [payload.fieldId]: payload.value,
                };

                await TABLE_API.updateRecord(this.tableId, payload.recordId, updatingFields);
                // Yerel kaydı güncelle
                // Vue.set() kullanarak reaktiflik sağlanır
                this.$message.success("Kayıt başarıyla güncellendi.");
                this.$set(this.record.data, payload.fieldId, payload.value);

                this.fetchData();
            } catch (error) {
                console.error("Kayıt güncellenirken hata oluştu:", error);
                this.$message.error("Kayıt güncellenirken hata oluştu");
            }
        },
        async fetchCommentCount() {
            try {
                const response = await listComments("tableRecord", this.recordId);
                this.commentCount = response.data?.data?.length || 0;
            } catch (error) {
                console.error("Failed to fetch comment count:", error);
            }
        },
        handleCommentsLoaded(comments) {
            this.commentCount = comments.length;
        },
    },
    async created() {
        this.table = this.tables.find((t) => t._id === this.tableId);
        await this.fetchData();
    },
    watch: {
        tableId: {
            immediate: true,
            handler(newVal) {
                this.table = this.tables.find((t) => t._id === newVal);
            },
        },
        tables: {
            immediate: true,
            handler(newVal) {
                this.table = newVal.find((t) => t._id === this.tableId);
            },
        },
    },
});
</script>

<style scoped lang="scss">
.record-viewer {
    padding: 16px;

    .record-container {
        width: 100%;
    }

    .field-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        width: 100%;
    }

    .field-item {
        flex: 1 1 calc(50% - 16px); // 2 sütunlu grid için
        min-width: 280px; // Minimum genişlik
        display: flex;
        flex-direction: column;
        gap: 4px;

        .field-label {
            font-weight: 500;
            color: rgba(0, 0, 0, 0.85);
            display: flex;
            align-items: center;
            gap: 8px;

            .field-icon {
                font-size: 16px;
                color: rgba(0, 0, 0, 0.45);
            }
        }
    }

    .empty-info {
        text-align: center;
        color: #999999;
        margin-top: 20px;
    }

    // Sekmelerin stillerini ayarlayabilirsiniz
    ::v-deep .ant-tabs-nav {
        margin-bottom: 16px;
    }
}
</style>
